import { useLocale, useTranslation } from '@dreamstack/i18n'
import { trpc } from '@dreamstack/trpc/client'
import { cn } from '@dreamstack/util/src/cn'
import { PROPERTY_STATUS_FREE, PropertyStatus } from '@dreamstack/real-estate-logic'
import includes from 'lodash/includes'
import map from 'lodash/map'
import dynamic from 'next/dynamic'
import type { FunctionComponent } from 'react'
import { SimpleAccordion } from '../Accordion/SimpleAccordion'
import { SimpleAccordionEntry } from '../Accordion/SimpleAccordionEntry'
import { BodyTextWithLinks } from '../layout/BodyTextWithLinks'
import { GridWithGaps } from '../layout/MainGrid'
import { HeadingH1 } from '../layout/Texts'
import { NewsHubPreview } from '../news/NewsHubPreview'
import { TriggerSearchProfilePopup } from '../tracking/unbounce/TriggerSearchProfilePopup'
import { useParsePropertyStatus } from '../hooks/usePropertyStatus'
import { getFilteredPropOrProjectImages } from './helper/utils'
import { EnergyCertificate } from './EnergyCertificate'
import { MoreFlatsList } from './MoreFlatsList'
import { NearbyProperties } from './NearbyProperties'
import { PropertyContact } from './PropertyContact'
import { PropertyDocuments } from './PropertyDocuments'
import { PropertyImageSlider } from './PropertyImageSlider'
import { PropertyInformation } from './PropertyInformation'

// Dynamic import for Google Maps - reduces initial bundle size
const PropertyMap = dynamic(
  () => import('./map/PropertyMap').then((mod) => mod.PropertyMap),
  { ssr: false }
)
import { PropertyVirtualRoundtrip } from './PropertyVirtualRoundtrip'
import { useFetchFullPropertyById } from './useFetchFullPropertyById'

const PropertyDetailGrid = ({
  characterCount,
  className,
  children,
  ...rest
}: React.PropsWithChildren<{
  characterCount: number
  className?: string
}>) => {
  let topMarginClass = ''
  if (characterCount <= 40) topMarginClass = '-mt-px-196'
  else if (characterCount <= 60) topMarginClass = '-mt-px-160'
  else topMarginClass = '-mt-px-128'

  const extraLongTitleClasses =
    characterCount > 60 ? 'ml:mt-[-130px] lg:-mt-px-80' : ''

  return (
    <GridWithGaps
      className={cn(
        'col-span-12 bg-accentroWhite-full px-px-24 md:mt-[-100px] ml:-mt-px-128 ml:px-px-48',
        topMarginClass,
        extraLongTitleClasses,
        className
      )}
      {...rest}
    >
      {children}
    </GridWithGaps>
  )
}

const ArticlesLayout = ({
  className,
  children,
  ...rest
}: React.PropsWithChildren<{ className?: string }>) => (
  <div
    className={cn(
      'grid col-span-full grid-cols-1 gap-px-64 md:grid-cols-2 md:gap-px-48',
      className
    )}
    {...rest}
  >
    {children}
  </div>
)

export const PropertyDetail: FunctionComponent<
  React.PropsWithChildren<{
    id: number
  }>
> = ({ id }) => {
  const t = useTranslation()
  const locale = useLocale()

  const { property } = useFetchFullPropertyById({ id })
  const { data: topProps } = trpc.property.getTopPropertiesForRegion.useQuery(
    {
      locale,
      region: property?.region ?? undefined,
      id: property?.id ?? 0,
      rs_type: property?.rs_type || 'APARTMENT',
      status: [
        PropertyStatus.FREE,
        PropertyStatus.FREE_STOP_AD,
        PropertyStatus.PRERESERVED,
      ],
    },
    { trpc: { ssr: true }, staleTime: Infinity, enabled: !!property?.id }
  )

  const { data: allProps } = trpc.property.getPropertiesByProjectId.useQuery(
    {
      locale,
      id: property?.id ?? 0,
      projectId: property?.project_id ?? undefined,
    },
    { trpc: { ssr: true }, staleTime: Infinity, enabled: !!property?.id }
  )

  const propTitle = property?.title
  const characterCount = propTitle?.length ?? 0
  const showTopProps = !!topProps?.length
  const showMoreFlats = !!allProps?.length
  const { filteredImages, floorPlan } = !!property?.property_images
    ? getFilteredPropOrProjectImages(property?.property_images)
    : { filteredImages: [], floorPlan: undefined }
  const imagesWithoutFirstImage = filteredImages.slice(1)
  const hasPrice = !(property?.price == null)
  const status = property?.status
  const getPropertyStatus = useParsePropertyStatus()
  const propertyStatus = getPropertyStatus(property?.status)
  const showStatusLabel = !includes(PROPERTY_STATUS_FREE, status)
  const statusLabel = showStatusLabel ? propertyStatus : ''

  const propertyLinks = property?.custom_fields?.find(
    (field) => field.key === 'links'
  )?.value

  const floorfyLink = propertyLinks?.find((link: any) =>
    link.url.toLowerCase().includes('floorfy')
  )

  const energyCertificate = property

  const city = property?.city
  const rented = property?.rented

  const { data: filteredArticles } =
    trpc.cms.getNewsArticlePreviewsForPropertyDetails.useQuery(
      {
        locale,
        rented,
        city,
      },
      { trpc: { ssr: true }, staleTime: Infinity }
    )

  if (!property) return null

  return (
    <PropertyDetailGrid characterCount={characterCount}>
      <GridWithGaps className="col-span-12 ">
        <PropertyInformation property={property} statusLabel={statusLabel} />
        <PropertyContact {...property} small={true} morePadding={false} />
      </GridWithGaps>
      <PropertyImageSlider
        hasPrice={hasPrice}
        images={imagesWithoutFirstImage}
        property={property}
      />

      <PropertyVirtualRoundtrip floorfyLink={floorfyLink} />

      <div className="col-span-12">
        <SimpleAccordion>
          {!!property.description_note && (
            <SimpleAccordionEntry title={t('accentro:description')}>
              <BodyTextWithLinks>{property.description_note}</BodyTextWithLinks>
            </SimpleAccordionEntry>
          )}
          {(property.location_note || property.geoLocation) && (
            <SimpleAccordionEntry
              title={t('accentro:location_lage')}
              className="location-accordion"
            >
              <BodyTextWithLinks>{property.location_note}</BodyTextWithLinks>
              <div className="absolute left-0 right-0 mt-px-16">
                <PropertyMap property={property} />
              </div>
              <div className="h-[467px] md:h-[368px] ml:h-[567px]" />
            </SimpleAccordionEntry>
          )}
          {!!property.furnishing_note && (
            <SimpleAccordionEntry title={t('accentro:furnishing')}>
              <BodyTextWithLinks>{property.furnishing_note}</BodyTextWithLinks>
            </SimpleAccordionEntry>
          )}
          {!!energyCertificate && (
            <SimpleAccordionEntry
              title={t('accentro:property.energyCertificate')}
            >
              <EnergyCertificate certificate={energyCertificate} />
            </SimpleAccordionEntry>
          )}
        </SimpleAccordion>
      </div>
      <PropertyDocuments floorPlan={floorPlan} floorfyLink={floorfyLink} />

      {showMoreFlats && (
        <>
          <HeadingH1 className="col-span-12 mt-px-48 ml:mt-px-80 lg:mt-px-128">
            {t('accentro:propertyDetails.more-in-property')}
          </HeadingH1>
          <MoreFlatsList properties={allProps} />
        </>
      )}

      {!!filteredArticles && (
        <div className="mt-px-48 ml:mt-px-64 col-span-12 py-px-48 px-px-24 bg-accentroAquaVeryLight-full md:py-px-64 md:px-px-48">
          <HeadingH1>{t('accentro:propertyDetails.ourGuide')}</HeadingH1>
          <GridWithGaps className="mt-px-48">
            <ArticlesLayout>
              {map(filteredArticles, (article, i) => {
                return <NewsHubPreview key={i} article={article} />
              })}
            </ArticlesLayout>
          </GridWithGaps>
        </div>
      )}
      {showTopProps && (
        <>
          <HeadingH1 className="col-span-12 mt-px-48 ml:mt-px-80 lg:mt-px-128">
            {t('accentro:propertyDetails.more-in-region')}
          </HeadingH1>
          <NearbyProperties properties={topProps} />
        </>
      )}
      <TriggerSearchProfilePopup />
    </PropertyDetailGrid>
  )
}
